import type { FAQType } from '../../components/faq'
import ContentPrecisionIcon from '../../icons/content/content-precision'
import DescriptionKeyIcon from '../../icons/content/description-key'
import ImageComplianceIcon from '../../icons/content/image-compliance'
import ImageCountKeyIcon from '../../icons/content/image-count-key'
import ImageCountMonitorIcon from '../../icons/content/image-count-monitor'
import ImageKeyIcon from '../../icons/content/image-key'
import KeywordGapIcon from '../../icons/content/keyword-gap'
import RetailerContentIcon from '../../icons/content/retailer-content'
import TitleKeyIcon from '../../icons/content/title-key'
import UserFriendlyIcon from '../../icons/content/user-friendly'
import ValidationIcon from '../../icons/content/validation'
import type {
  CardType,
  InfiniteScrollType,
  KeyFeatureType,
  SolutionEmpowersType,
  Stat,
} from '../solutions'

const CONTENT_LOGOS: InfiniteScrollType[] = [
  {
    src: '/images/solutions/brands/johnson-johnson.png',
    alt: 'Johnson & Johnson',
    width: 207,
  },
  {
    src: '/images/solutions/brands/eti.png',
    alt: 'Eti',
    width: 62,
  },
  {
    src: '/images/solutions/brands/colgate.png',
    alt: 'Colgate',
    width: 92,
  },
  {
    src: '/images/solutions/brands/ferrero.png',
    alt: 'Ferrero',
    width: 244,
  },
  {
    src: '/images/solutions/brands/nestle.png',
    alt: 'Nestle',
    width: 113,
  },
  {
    src: '/images/solutions/brands/naos.png',
    alt: 'Naos',
    width: 162,
  },
  {
    src: '/images/solutions/brands/fellas.png',
    alt: 'Fellas',
    width: 185,
  },
]

const CARDS: CardType[] = [
  {
    title: 'Content Precision',
    content: 'Monitor title, description, and image accuracy for best results.',
    Icon: ContentPrecisionIcon,
  },
  {
    title: 'Title & Description Validation',
    content: 'Ensure titles and descriptions adhere to your standards.',
    Icon: ValidationIcon,
  },
  {
    title: 'Image Compliance Check',
    content: 'Validate product image accuracy and count across retailers.',
    Icon: ImageComplianceIcon,
  },
]

const CONTENT_KEY_FEATURES: KeyFeatureType[] = [
  {
    key: 'title',
    title: 'Title',
    content:
      'Refine product appeal with optimized titles using our content panel. Monitor all titles on a single dashboard. Ensure accuracy and keyword inclusion to boost search relevance and engagement.',
    Icon: TitleKeyIcon,
  },
  {
    key: 'description',
    title: 'Description',
    content:
      'Check product descriptions for essential information and selling points as part of your e-commerce content optimization strategy. Enhance your product narratives to engage and inform. Achieve clarity, searchability, and higher conversion rates.',
    Icon: DescriptionKeyIcon,
  },
  {
    key: 'image',
    title: 'Image',
    content:
      'Ensure your product images are top-notch. Check and validate the main image and additional visuals for each product. Confirm their quality and relevance. Prioritize high-quality images to attract customers and represent your products accurately.',
    Icon: ImageKeyIcon,
  },
  {
    key: 'image-count',
    title: 'Image Count',
    content:
      'Verify the number of images per product across various retailers. Maintain a consistent image count for a comprehensive visual representation. Meet retailer standards and consumer expectations. This contributes to a stronger online presence.',
    Icon: ImageCountKeyIcon,
  },
]

const CONTENT_SOLUTION_EMPOWERS: SolutionEmpowersType[] = [
  {
    key: 'keyword-gap-analysis',
    title: 'Keyword Gap Analysis',
    content: 'Detect missing essential keywords in product listings.',
    Icon: KeywordGapIcon,
  },
  {
    key: 'retailer-content-comparison',
    title: 'Retailer Content Comparison',
    content: 'Ensure retail content consistency & compliance.',
    Icon: RetailerContentIcon,
  },
  {
    key: 'image-count-monitoring',
    title: 'Image Count Monitoring',
    content: 'Assess image quantity per product against targets.',
    Icon: ImageCountMonitorIcon,
  },
  {
    key: 'user-friendly-dashboard',
    title: 'User-Friendly Dashboard',
    content: 'Navigate easily for efficient content management.',
    Icon: UserFriendlyIcon,
  },
]

const CONTENT_FAQS: FAQType['items'] = [
  {
    trigger:
      'Why is monitoring e-commerce content accuracy important for my business?',
    content:
      'Accuracy in your e-commerce content is key to building trust with customers. In addition, it improves your presence in the e-commerce product search engines. Accurate titles, descriptions, and images help in better search engine ranking, align with customer expectations, and improve the overall shopping experience.',
    key: 'item-1',
  },
  {
    trigger:
      "How does Mindsite's Content Panel improve my online product listings?",
    content:
      "Our Content Panel is one of the leading e-commerce search solutions, offering features to monitor and optimize your online product listings. By analyzing keyword gaps, image counts, and content alignment across retailers, it ensures your listings are complete, accurate, and in line with your content guidelines. This enhances your product's online presence and appeal.",
    key: 'item-2',
  },
  {
    trigger:
      'Is the Content Panel user-friendly for newbies to e-commerce optimization?',
    content:
      "Absolutely. Our Content Panel is an intuitive part of our e-commerce site search solutions, facilitating content monitoring and optimization. It's easy to use, so anyone can manage and enhance their e-commerce content for better search engine optimization.",
    key: 'item-3',
  },
  {
    trigger: 'How does the Content Panel contribute to increased online sales?',
    content:
      'The Content Panel enhances online sales by optimizing e-commerce content for better visibility and appeal. Accurate and engaging product descriptions and images improve discoverability, leading to higher engagement and conversion rates. This optimization directly aligns with e-commerce search metrics, which are crucial to sales growth.',
    key: 'item-4',
  },
  {
    trigger:
      'Can the Content Panel help align my products with retailer standards?',
    content:
      'Yes, the Content Panel assists in aligning your product listings with various retailer standards, ensuring they are optimized for e-commerce product search. This alignment guarantees consistency and compliance, key to maintaining a strong online presence across different platforms.',
    key: 'item-5',
  },
]

const CONTENT_STATS: Stat[] = [
  {
    value: '99%',
    info: 'content compliance within 6 months.',
  },
  {
    value: '50%',
    info: 'increase in conversion rates.',
  },
]

export {
  CARDS,
  CONTENT_FAQS,
  CONTENT_KEY_FEATURES,
  CONTENT_LOGOS,
  CONTENT_SOLUTION_EMPOWERS,
  CONTENT_STATS,
}
